import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';

import PencilIcon from '../../components/Icons/PencilIcon';
import { DASHBOARD_STACK } from '../../utils/screenNames';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import ClubbieModal from '../../components/Modal/ClubbieModal';
import { deleteTournamentThunk, fetchTournamentDashboard } from '../../redux/slices/tournamentSlice';
import UpdateTournamentType from '../../components/UpdateTournament/UpdateTournamentType';
import UpdateTournamentNumberOfPitches from '../../components/UpdateTournamentNoOfPitches/UpdateTournamentNoOfPitches';
import UpdateTournamentSchedule from '../../components/UpdateTournamentSchedule/UpdateTournamentSchedule';
import UpdateTournamentTeams from '../../components/UpdateTournamentTeams/UpdateTournamentTeams';
import UpdateTournamentVenueName from '../../components/UpdateTournamentVenueName/UpdateTournamentVenueName';
import EmptyState from '../../components/Dashboard/EmptyState';
import DashboardHeader from '../../components/Dashboard/DashboardHeader';
import DashboardStatusRow from '../../components/Dashboard/DashboardStatusRow';
import DashboardStatistics from '../../components/Dashboard/DashboardStatistics';
import DashboardKnockoutProgressing from '../../components/Dashboard/DashboardKnockoutProgressing';
import DashboardFixturesRow from '../../components/Dashboard/DashboardFixturesRow';
import DashboardPaymentSummary from '../../components/Dashboard/DashboardPaymentSummary';
import LockStagesModal from '../../components/Dashboard/LockStagesModal';
import CompetitionsEmptyState from '../competitions/CompetitionsEmptyState';
import useTournamentRole from '../../hooks/useTournamentRole';
import EditCompetitionModal from '../../components/EditCompetitionModal/EditCompetitionModal';

/**
 * HomeV2 - Modern JSX component with Tailwind CSS
 * Tournament/Competition dashboard showing details and edit options
 * Preserves all logic from Home.js with Tailwind styling
 */
function HomeV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [isPitchModalOpen, setIsPitchModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLockStagesModalOpen, setIsLockStagesModalOpen] = useState(false);

  const { selectedTournament, selectedCategory, loading, tournaments } = useSelector(
    (state) => state.tournament
  );
  const { role, permissions } = useTournamentRole();

  const selectedTournamentId = selectedTournament?.id || selectedTournament?._id;

  useEffect(() => {
    if (!selectedTournamentId) return;

    dispatch(fetchTournamentDashboard(selectedTournamentId));

    // Refetch when the tab returns to visible so a long-open page doesn't
    // show fixtures whose matchTime has since slipped into the past. Throttled
    // so quick tab-flipping doesn't hammer the API.
    let lastRefetch = Date.now();
    const REFETCH_THROTTLE_MS = 30_000;

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefetch < REFETCH_THROTTLE_MS) return;
      lastRefetch = Date.now();
      dispatch(fetchTournamentDashboard(selectedTournamentId));
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [dispatch, selectedTournamentId]);

  const handleDeleteTournament = async (tournamentId) => {
    try {
      setIsDeleting(true);
      await dispatch(deleteTournamentThunk(tournamentId)).unwrap();
      toast.success('Tournament deleted successfully');
      navigate('/tournaments');
    } catch (error) {
      toast.error(error.message || 'Failed to delete tournament');
    } finally {
      setIsDeleting(false);
    }
  };

  const DetailRow = ({ label, value, onClick }) => (
    <div className="flex items-center justify-between py-4 border-b border-[#192634] last:border-b-0">
      <div className="flex-1">
        <div className="text-[#7F8585] text-sm mb-1">{label}</div>
        <div className="text-app-text font-medium">{value}</div>
      </div>
      <button
        onClick={onClick}
        className="ml-4 p-2 hover:bg-[#082441] rounded-lg transition-colors"
      >
        <PencilIcon className="text-[#00E7FF]" />
      </button>
    </div>
  );

  if (loading) {
    return <OverlayLoader />;
  }

  if (!selectedTournament) {
    if (!tournaments || tournaments.length === 0) {
      return <CompetitionsEmptyState />;
    }
    return (
      <div className="h-full bg-[#000F1F] flex flex-col">
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000F1F] text-app-text">
      <div className="max-w-[1400px] mx-auto py-4 px-3 md:py-6 md:px-6">
        <div className="mb-4">
          <DashboardHeader
            onEdit={permissions.canEditSettings ? () => setIsEditModalOpen(true) : undefined}
            onCopyLink={() => {
              const publicUrl = `${window.location.origin}/public/${selectedTournament?.id}`;
              navigator.clipboard.writeText(publicUrl);
              toast.success('Public URL copied to clipboard!');
            }}
            onDelete={permissions.canDeleteTournament ? () => setIsDeleteModalOpen(true) : undefined}
            onLockStages={role === 'owner' ? () => setIsLockStagesModalOpen(true) : undefined}
          />
        </div>

        <div className="mb-6">
          <DashboardStatusRow />
        </div>

        <div className="mb-6">
          <DashboardStatistics />
        </div>

        <div className="mb-6">
          <DashboardKnockoutProgressing />
        </div>

        <div className="mb-6">
          <DashboardFixturesRow />
        </div>

        <div className="mb-6">
          <DashboardPaymentSummary />
        </div>

        {/* About This Competition */}
        {/* <div className="mb-8">
          <h3 className="text-xl font-semibold text-app-text mb-4">
            About This Competition
          </h3>
          <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6">
            <DetailRow
              label="Format"
              value={selectedTournament.type}
              onClick={() => setIsTypeModalOpen(true)}
            />
            <DetailRow
              label="Teams Registered"
              value={`${selectedTournament.divisions?.reduce(
                (total, div) => total + (div.numberOfTeams || 0),
                0
              )} Teams`}
              onClick={() => setIsTeamModalOpen(true)}
            />
            <DetailRow
              label="Venue"
              value={selectedTournament.matchVenue?.venue || 'Not set'}
              onClick={() => setIsVenueModalOpen(true)}
            />
            <DetailRow
              label="Number of Pitches"
              value={selectedTournament.matchVenue?.numberOfPitches || 'Not set'}
              onClick={() => setIsPitchModalOpen(true)}
            />
          </div>
        </div> */}

        {/* About Category */}
        {/* {selectedCategory && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-app-text mb-4">
              About Category
            </h3>
            <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6">
              <DetailRow
                label="Name"
                value={selectedCategory.name || selectedCategory.label}
                onClick={() => setIsDivisionNameModalOpen(true)}
              />
              <DetailRow
                label="Teams"
                value={`${selectedCategory.teams?.length || 0} Teams`}
                onClick={() => setIsDivisionTeamModalOpen(true)}
              />
            </div>
          </div>
        )} */}

        {/* Modals */}
        <LockStagesModal
          isOpen={isLockStagesModalOpen}
          onClose={() => setIsLockStagesModalOpen(false)}
        />
        <EditCompetitionModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
        />
        {isTypeModalOpen && (
          <UpdateTournamentType
            isOpen={isTypeModalOpen}
            onClose={() => setIsTypeModalOpen(false)}
          />
        )}
        {isTeamModalOpen && (
          <UpdateTournamentTeams
            isOpen={isTeamModalOpen}
            onClose={() => setIsTeamModalOpen(false)}
          />
        )}
        {isScheduleModalOpen && (
          <UpdateTournamentSchedule
            isOpen={isScheduleModalOpen}
            onClose={() => setIsScheduleModalOpen(false)}
          />
        )}
        {isVenueModalOpen && (
          <UpdateTournamentVenueName
            isOpen={isVenueModalOpen}
            onClose={() => setIsVenueModalOpen(false)}
          />
        )}
        {isPitchModalOpen && (
          <UpdateTournamentNumberOfPitches
            isOpen={isPitchModalOpen}
            onClose={() => setIsPitchModalOpen(false)}
          />
        )}
        {/* Delete Confirmation Modal */}
        <ClubbieModal
          isOpen={isDeleteModalOpen}
          toggle={() => setIsDeleteModalOpen(false)}
          width="500px"
          title="Delete Competition"
          footer={
            <div className="flex justify-end gap-3 w-full">
              <button
                type="button"
                className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-3 rounded-full bg-app-error hover:bg-red-600 text-blue-shade-8 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleDeleteTournament(selectedTournament.id)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          }
        >
          <p className="text-app-text-secondary text-[13px]">
            Are you sure you want to delete this competition? This action cannot be undone.
          </p>
        </ClubbieModal>
      </div>
    </div>
  );
}

export default HomeV2;
